import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Draw as DrawIcon,
} from "@mui/icons-material";
import YAML from "yaml";
import Header from "../../components/Header";
import DrawingCanvas from "../../components/DrawingCanvas";
import saasApi from "../../utils/saasApi";
import { SHOPLIFTING_MODES } from "./shopliftingRules";

const RESERVED_KEYS = new Set(["version", "detectors", "rules", "cameras"]);
const cloneJson = (value) => JSON.parse(JSON.stringify(value ?? {}));
const pretty = (value) => JSON.stringify(value ?? {}, null, 2);
const isPlainObject = (value) => value && typeof value === "object" && !Array.isArray(value);

const detectorMapFrom = (configuration) => {
  if (isPlainObject(configuration?.detectors)) return configuration.detectors;
  return Object.fromEntries(
    Object.entries(configuration || {}).filter(([key, value]) => !RESERVED_KEYS.has(key) && isPlainObject(value))
  );
};

const usesNestedDetectors = (configuration) => isPlainObject(configuration?.detectors);

const setDetectorValue = (configuration, detector, key, value) => {
  if (usesNestedDetectors(configuration)) {
    return {
      ...configuration,
      detectors: {
        ...(configuration.detectors || {}),
        [detector]: {
          ...(configuration.detectors?.[detector] || {}),
          [key]: value,
        },
      },
    };
  }
  return {
    ...configuration,
    [detector]: {
      ...(configuration?.[detector] || {}),
      [key]: value,
    },
  };
};

const coerceNumber = (raw, fallback) => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const pointFrom = (value, fallback = [0.5, 0.5]) => (
  Array.isArray(value) && value.length >= 2 ? [Number(value[0]) || 0, Number(value[1]) || 0] : fallback
);

const JsonValueEditor = ({ label, value, onCommit }) => {
  const [text, setText] = useState(pretty(value));
  const [error, setError] = useState("");

  useEffect(() => {
    setText(pretty(value));
    setError("");
  }, [value]);

  const commit = () => {
    try {
      onCommit(JSON.parse(text));
      setError("");
    } catch (err) {
      setError(err.message || "JSON invalido");
    }
  };

  return (
    <TextField
      label={label}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      multiline
      minRows={Array.isArray(value) ? 3 : 5}
      fullWidth
      error={Boolean(error)}
      helperText={error || "JSON: se aplica al salir del campo"}
      spellCheck={false}
    />
  );
};

const ClassesEditor = ({ value, onChange }) => {
  const classes = Array.isArray(value) ? value.map(Number).filter(Number.isFinite) : [];
  const [newId, setNewId] = useState("");
  const toggle = (id) => {
    onChange(classes.includes(id) ? classes.filter((item) => item !== id) : [...classes, id].sort((a, b) => a - b));
  };
  const add = () => {
    const id = Number(newId);
    if (!Number.isFinite(id)) return;
    onChange([...new Set([...classes, id])].sort((a, b) => a - b));
    setNewId("");
  };
  return (
    <Stack spacing={1}>
      <Typography variant="body2">classes</Typography>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {[16, 17].map((id) => (
          <Chip key={id} label={id === 16 ? "16 dog" : "17 cat"} color={classes.includes(id) ? "primary" : "default"} onClick={() => toggle(id)} />
        ))}
        {classes.map((id) => (
          <Chip key={id} label={`ID ${id}`} variant="outlined" onDelete={() => onChange(classes.filter((item) => item !== id))} />
        ))}
      </Stack>
      <Stack direction="row" gap={1}>
        <TextField size="small" label="Agregar ID" type="number" value={newId} onChange={(event) => setNewId(event.target.value)} />
        <Button variant="outlined" onClick={add}>Agregar</Button>
      </Stack>
    </Stack>
  );
};

const LinesEditor = ({ value, onChange, onDraw }) => {
  const lines = Array.isArray(value) ? value : [];
  const updateLine = (idx, patch) => {
    onChange(lines.map((line, lineIdx) => (lineIdx === idx ? { ...line, ...patch } : line)));
  };
  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Typography variant="body2">lines</Typography>
        <Stack direction="row" gap={1}>
          <Button size="small" variant="outlined" startIcon={<DrawIcon />} onClick={onDraw}>Dibujar</Button>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onChange([...lines, { start: [0.5, 0], end: [0.5, 1] }])}>Agregar</Button>
        </Stack>
      </Stack>
      {!lines.length && <Alert severity="info">No hay lineas configuradas.</Alert>}
      {lines.map((line, idx) => {
        const start = pointFrom(line.start, [0.5, 0]);
        const end = pointFrom(line.end, [0.5, 1]);
        return (
          <Card key={idx} variant="outlined" sx={{ borderRadius: "8px" }}>
            <CardContent sx={{ py: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Typography variant="subtitle2">Linea {idx + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => onChange(lines.filter((_, lineIdx) => lineIdx !== idx))}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                {["start", "end"].map((field) => {
                  const point = field === "start" ? start : end;
                  return (
                    <Grid item xs={12} md={6} key={field}>
                      <Typography variant="caption">{field}</Typography>
                      <Stack direction="row" gap={1} sx={{ mt: 0.5 }}>
                        <TextField size="small" label="x" type="number" value={point[0]} inputProps={{ step: 0.01, min: 0, max: 1 }} onChange={(event) => updateLine(idx, { [field]: [coerceNumber(event.target.value, point[0]), point[1]] })} />
                        <TextField size="small" label="y" type="number" value={point[1]} inputProps={{ step: 0.01, min: 0, max: 1 }} onChange={(event) => updateLine(idx, { [field]: [point[0], coerceNumber(event.target.value, point[1])] })} />
                      </Stack>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

const PointEditor = ({ label, value, onChange }) => {
  const point = pointFrom(value);
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{label}</Typography>
      <Stack direction="row" gap={1}>
        <TextField label="x" type="number" value={point[0]} inputProps={{ step: 0.01, min: 0, max: 1 }} onChange={(event) => onChange([coerceNumber(event.target.value, point[0]), point[1]])} fullWidth />
        <TextField label="y" type="number" value={point[1]} inputProps={{ step: 0.01, min: 0, max: 1 }} onChange={(event) => onChange([point[0], coerceNumber(event.target.value, point[1])])} fullWidth />
      </Stack>
    </Stack>
  );
};

const ConfigValueEditor = ({ name, value, onChange, onDrawLines, onDrawQueue, onDrawPolygon }) => {
  if (name === "classes" && Array.isArray(value)) return <ClassesEditor value={value} onChange={onChange} />;
  if (name === "lines" && Array.isArray(value)) return <LinesEditor value={value} onChange={onChange} onDraw={onDrawLines} />;
  if ((name === "line_start" || name === "line_end") && Array.isArray(value)) {
    return (
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <PointEditor label={name} value={value} onChange={onChange} />
          {name === "line_start" && <Button variant="outlined" startIcon={<DrawIcon />} onClick={onDrawQueue}>Dibujar linea</Button>}
        </Stack>
      </Stack>
    );
  }
  if (name === "region_points" && Array.isArray(value)) {
    return (
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">region_points</Typography>
          <Button size="small" variant="outlined" startIcon={<DrawIcon />} onClick={onDrawPolygon}>Dibujar region</Button>
        </Stack>
        <JsonValueEditor label="region_points" value={value} onCommit={onChange} />
      </Stack>
    );
  }
  if (typeof value === "boolean") {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
        <Typography variant="body2">{name}</Typography>
        <Switch checked={value} onChange={(event) => onChange(event.target.checked)} />
      </Stack>
    );
  }
  if (typeof value === "number") {
    const isRatio = value >= 0 && value <= 1 && /threshold|confidence|conf|iou|alpha|sensitivity|ratio/i.test(name);
    return (
      <TextField label={name} type="number" value={value} inputProps={{ step: isRatio ? 0.01 : 1, min: isRatio ? 0 : undefined, max: isRatio ? 1 : undefined }} onChange={(event) => onChange(coerceNumber(event.target.value, value))} fullWidth />
    );
  }
  if (typeof value === "string" && name === "modo_hurto") {
    const mode = SHOPLIFTING_MODES.find((option) => option.value === value);
    return (
      <TextField select label="Forma de detectar el robo (modo_hurto)" value={value} onChange={(event) => onChange(event.target.value)} helperText={mode?.help || "Valor no valido: elegir una opcion"} fullWidth>
        {SHOPLIFTING_MODES.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        {!mode && <MenuItem value={value}>{value} (no valido)</MenuItem>}
      </TextField>
    );
  }
  if (typeof value === "string" && name === "direction") {
    return (
      <TextField select label={name} value={value} onChange={(event) => onChange(event.target.value)} fullWidth>
        {["down", "up", "left", "right"].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
      </TextField>
    );
  }
  if (typeof value === "string") return <TextField label={name} value={value} onChange={(event) => onChange(event.target.value)} fullWidth />;
  if (Array.isArray(value) || isPlainObject(value)) return <JsonValueEditor label={name} value={value} onCommit={onChange} />;
  return <TextField label={name} value={value == null ? "" : String(value)} onChange={(event) => onChange(event.target.value)} fullWidth />;
};

const RulesEditor = ({ value, onChange, onError }) => {
  const rulesConfig = isPlainObject(value) ? value : {};
  const [newSection, setNewSection] = useState("");

  const updateSection = (sectionName, nextSection) => {
    onChange({ ...rulesConfig, [sectionName]: nextSection });
  };

  const renameSection = (oldName, nextName) => {
    const cleanName = nextName.trim();
    if (!cleanName || cleanName === oldName) return;
    if (Object.prototype.hasOwnProperty.call(rulesConfig, cleanName)) {
      onError?.(`Ya existe una seccion llamada ${cleanName}`);
      return;
    }
    const nextRules = { ...rulesConfig };
    nextRules[cleanName] = nextRules[oldName];
    delete nextRules[oldName];
    onChange(nextRules);
  };

  const deleteSection = (sectionName) => {
    const nextRules = { ...rulesConfig };
    delete nextRules[sectionName];
    onChange(nextRules);
  };

  const addSection = () => {
    const cleanName = newSection.trim();
    if (!cleanName) return;
    if (Object.prototype.hasOwnProperty.call(rulesConfig, cleanName)) {
      onError?.(`Ya existe una seccion llamada ${cleanName}`);
      return;
    }
    onChange({
      ...rulesConfig,
      [cleanName]: { frame_window: 50, rules: [] },
    });
    setNewSection("");
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
        <TextField size="small" label="Nueva seccion" value={newSection} onChange={(event) => setNewSection(event.target.value)} fullWidth />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addSection}>Agregar</Button>
      </Stack>
      {!Object.keys(rulesConfig).length && <Alert severity="info">No hay reglas configuradas.</Alert>}
      {Object.entries(rulesConfig).map(([sectionName, sectionValue]) => {
        const section = isPlainObject(sectionValue) ? sectionValue : {};
        const sectionRules = Array.isArray(section.rules) ? section.rules : [];
        const sectionExtra = Object.fromEntries(
          Object.entries(section).filter(([key]) => !["frame_window", "rules"].includes(key))
        );

        const updateRule = (ruleIdx, nextRule) => {
          updateSection(sectionName, {
            ...section,
            rules: sectionRules.map((rule, idx) => (idx === ruleIdx ? nextRule : rule)),
          });
        };

        return (
          <Box key={sectionName} sx={{ border: 1, borderColor: "divider", borderRadius: "8px", p: 1.5 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  label="Seccion"
                  defaultValue={sectionName}
                  onBlur={(event) => renameSection(sectionName, event.target.value)}
                  fullWidth
                />
                <IconButton size="small" color="error" onClick={() => deleteSection(sectionName)}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
              <TextField
                size="small"
                label="frame_window"
                type="number"
                value={coerceNumber(section.frame_window, 0)}
                onChange={(event) => updateSection(sectionName, { ...section, frame_window: coerceNumber(event.target.value, section.frame_window) })}
                fullWidth
              />
              {Object.keys(sectionExtra).length > 0 && (
                <JsonValueEditor
                  label="Campos extra de seccion"
                  value={sectionExtra}
                  onCommit={(extra) => {
                    if (!isPlainObject(extra)) {
                      onError?.("Los campos extra de seccion deben ser un objeto JSON");
                      return;
                    }
                    updateSection(sectionName, { frame_window: section.frame_window, rules: sectionRules, ...extra });
                  }}
                />
              )}
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Typography variant="subtitle2">Reglas ({sectionRules.length})</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => updateSection(sectionName, {
                    ...section,
                    rules: [...sectionRules, { name: "new_rule", description: "", thresholds: {}, min_continuous: {} }],
                  })}
                >
                  Agregar regla
                </Button>
              </Stack>
              {sectionRules.map((ruleValue, ruleIdx) => {
                const rule = isPlainObject(ruleValue) ? ruleValue : {};
                const ruleExtra = Object.fromEntries(
                  Object.entries(rule).filter(([key]) => !["name", "description", "thresholds", "min_continuous"].includes(key))
                );
                return (
                  <Box key={`${sectionName}-${ruleIdx}`} sx={{ borderLeft: 3, borderColor: "primary.main", pl: 1.5 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <TextField
                          size="small"
                          label="name"
                          value={rule.name || ""}
                          onChange={(event) => updateRule(ruleIdx, { ...rule, name: event.target.value })}
                          fullWidth
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => updateSection(sectionName, { ...section, rules: sectionRules.filter((_, idx) => idx !== ruleIdx) })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <TextField
                        size="small"
                        label="description"
                        value={rule.description || ""}
                        onChange={(event) => updateRule(ruleIdx, { ...rule, description: event.target.value })}
                        fullWidth
                      />
                      <JsonValueEditor label="thresholds" value={isPlainObject(rule.thresholds) ? rule.thresholds : {}} onCommit={(next) => updateRule(ruleIdx, { ...rule, thresholds: next })} />
                      <JsonValueEditor label="min_continuous" value={isPlainObject(rule.min_continuous) ? rule.min_continuous : {}} onCommit={(next) => updateRule(ruleIdx, { ...rule, min_continuous: next })} />
                      {Object.keys(ruleExtra).length > 0 && (
                        <JsonValueEditor
                          label="Campos extra de regla"
                          value={ruleExtra}
                          onCommit={(extra) => {
                            if (!isPlainObject(extra)) {
                              onError?.("Los campos extra de regla deben ser un objeto JSON");
                              return;
                            }
                            updateRule(ruleIdx, {
                              name: rule.name,
                              description: rule.description,
                              thresholds: rule.thresholds,
                              min_continuous: rule.min_continuous,
                              ...extra,
                            });
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default function DetectionsYamlEditor() {
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [selectedDetector, setSelectedDetector] = useState("");
  const [tab, setTab] = useState("visual");
  const [manualFormat, setManualFormat] = useState("yaml");
  const [manualText, setManualText] = useState("");
  const [manualError, setManualError] = useState("");
  const [canvas, setCanvas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  const detectorMap = useMemo(() => detectorMapFrom(draft), [draft]);
  const detectors = useMemo(() => Object.keys(detectorMap).sort(), [detectorMap]);
  const dirty = useMemo(() => Boolean(original && draft && pretty(original) !== pretty(draft)), [original, draft]);
  const selectedConfig = selectedDetector ? detectorMap[selectedDetector] || {} : {};
  const nestedMode = usesNestedDetectors(draft);

  const syncManualText = useCallback((configuration = draft, format = manualFormat) => {
    if (!configuration) return;
    setManualText(format === "yaml" ? YAML.stringify(configuration) : pretty(configuration));
    setManualError("");
  }, [draft, manualFormat]);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus({ type: "", msg: "" });
    try {
      const configuration = await saasApi.getInternalDetectionsConfig();
      const next = cloneJson(configuration);
      setOriginal(next);
      setDraft(cloneJson(next));
      const names = Object.keys(detectorMapFrom(next)).sort();
      setSelectedDetector((current) => (current && names.includes(current) ? current : names[0] || ""));
      setManualText(manualFormat === "yaml" ? YAML.stringify(next) : pretty(next));
    } catch (err) {
      setStatus({ type: "error", msg: err.message || "No se pudo cargar detections.json/detections.yaml" });
    } finally {
      setLoading(false);
    }
  }, [manualFormat]);

  useEffect(() => {
    load();
  }, [load]);

  const updateTopLevel = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const updateDetectorField = (detector, key, value) => setDraft((current) => setDetectorValue(current, detector, key, value));

  const parseManual = () => {
    try {
      const parsed = manualFormat === "yaml" ? YAML.parse(manualText) : JSON.parse(manualText);
      if (!isPlainObject(parsed)) throw new Error("La configuracion debe ser un objeto");
      setDraft(parsed);
      const names = Object.keys(detectorMapFrom(parsed)).sort();
      setSelectedDetector((current) => (current && names.includes(current) ? current : names[0] || ""));
      setManualError("");
      setStatus({ type: "success", msg: "Editor manual aplicado al borrador. Revisa y guarda para enviar al motor." });
    } catch (err) {
      setManualError(err.message || "No se pudo parsear el contenido manual");
    }
  };

  const validateBeforeSave = () => {
    if (!isPlainObject(draft)) return "La configuracion debe ser un objeto JSON/YAML";
    if (!Object.keys(detectorMapFrom(draft)).length) return "No se encontraron detectores editables";
    if (draft.rules !== undefined && !isPlainObject(draft.rules)) return "rules debe ser un objeto JSON";
    return "";
  };

  const save = async () => {
    const validationError = validateBeforeSave();
    if (validationError) {
      setStatus({ type: "error", msg: validationError });
      return;
    }
    setSaving(true);
    setStatus({ type: "", msg: "" });
    try {
      const saved = await saasApi.saveInternalDetectionsConfig(draft);
      const next = cloneJson(saved);
      setOriginal(next);
      setDraft(cloneJson(next));
      const names = Object.keys(detectorMapFrom(next)).sort();
      setSelectedDetector((current) => (current && names.includes(current) ? current : names[0] || ""));
      syncManualText(next);
      setStatus({ type: "success", msg: "Configuracion de detecciones guardada. El motor la recarga sin reiniciarse." });
    } catch (err) {
      setStatus({ type: "error", msg: err.message || "No se pudo guardar detections.json/detections.yaml" });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    const next = cloneJson(original);
    setDraft(next);
    syncManualText(next);
    setStatus({ type: "success", msg: "Cambios descartados" });
  };

  const openCanvas = (mode, title, initialData, onSave) => setCanvas({ mode, title, initialData, onSave });
  const closeCanvas = () => setCanvas(null);

  const drawLines = () => openCanvas("lines", `Dibujar lineas - ${selectedDetector}`, selectedConfig.lines || [], (data) => {
    updateDetectorField(selectedDetector, "lines", data);
    closeCanvas();
  });

  const drawQueueLine = () => openCanvas("directional-line", `Dibujar linea queue - ${selectedDetector}`, {
    line_start: selectedConfig.line_start || [0, 0.5],
    line_end: selectedConfig.line_end || [1, 0.5],
    direction: selectedConfig.direction || "down",
  }, (data) => {
    updateDetectorField(selectedDetector, "line_start", data.line_start);
    updateDetectorField(selectedDetector, "line_end", data.line_end);
    updateDetectorField(selectedDetector, "direction", data.direction);
    closeCanvas();
  });

  const drawPolygon = () => openCanvas("polygon", `Dibujar region - ${selectedDetector}`, selectedConfig.region_points || [], (data) => {
    updateDetectorField(selectedDetector, "region_points", data);
    closeCanvas();
  });

  return (
    <Box m="20px">
      <Header title="DETECCIONES Y REGLAS" subtitle="Configuracion real del motor desde detections.json/detections.yaml" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {status.msg && <Alert severity={status.type === "error" ? "error" : "success"} sx={{ mb: 2 }}>{status.msg}</Alert>}

      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={load} disabled={loading || saving}>Recargar</Button>
        <Button variant="outlined" onClick={cancel} disabled={!dirty || loading || saving}>Cancelar cambios</Button>
        <Button variant="contained" onClick={save} disabled={!dirty || loading || saving}>Guardar cambios</Button>
        {dirty && <Chip color="warning" label="Cambios sin guardar" />}
        <Chip variant="outlined" label={nestedMode ? "Formato: detectors" : "Formato: detectores en raiz"} />
      </Stack>

      {!draft ? (
        <Alert severity="info">Cargando configuracion...</Alert>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, value) => {
            setTab(value);
            if (value === "manual") syncManualText();
          }} sx={{ mb: 2 }}>
            <Tab value="visual" label="Editor interactivo" />
            <Tab value="manual" label="Editor manual YAML/JSON" />
            <Tab value="full" label="Documento completo" />
          </Tabs>

          {tab === "visual" && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: "8px" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Detectores</Typography>
                    <TextField select label="Detector" value={selectedDetector} onChange={(event) => setSelectedDetector(event.target.value)} fullWidth disabled={!detectors.length}>
                      {detectors.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                    </TextField>
                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                      {detectors.map((name) => <Chip key={name} label={name} color={name === selectedDetector ? "primary" : "default"} onClick={() => setSelectedDetector(name)} />)}
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    {"version" in draft && <TextField label="version" type="number" value={draft.version} onChange={(event) => updateTopLevel("version", coerceNumber(event.target.value, draft.version))} fullWidth />}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card sx={{ borderRadius: "8px" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Parametros de {selectedDetector || "detector"}</Typography>
                    {!selectedDetector ? (
                      <Alert severity="info">No hay detectores en la configuracion recibida.</Alert>
                    ) : (
                      <Stack spacing={2}>
                        {Object.entries(selectedConfig).map(([key, value]) => (
                          <ConfigValueEditor
                            key={key}
                            name={key}
                            value={value}
                            onChange={(nextValue) => updateDetectorField(selectedDetector, key, nextValue)}
                            onDrawLines={drawLines}
                            onDrawQueue={drawQueueLine}
                            onDrawPolygon={drawPolygon}
                          />
                        ))}
                        {!Object.keys(selectedConfig).length && <Alert severity="info">Este detector no tiene parametros editables.</Alert>}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: "8px", mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Reglas</Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      El motor no usa estas reglas.
                      {"modo_hurto" in (detectorMapFrom(draft).shoplifting || {})
                        && " La forma de detectar el robo se elige en shoplifting, en \"Forma de detectar el robo\" (y por camara en Camaras)."}
                    </Alert>
                    <RulesEditor
                      value={draft.rules || {}}
                      onChange={(value) => updateTopLevel("rules", value)}
                      onError={(msg) => setStatus({ type: "error", msg })}
                    />
                  </CardContent>
                </Card>
                <Card sx={{ borderRadius: "8px" }}>
                  <CardContent>
                    <Typography variant="h6">Resumen</Typography>
                    <Typography variant="body2" color="text.secondary">{detectors.length} detectores configurados.</Typography>
                    <Typography variant="body2" color="text.secondary">El guardado envia el documento completo y preserva campos no editados.</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tab === "manual" && (
            <Card sx={{ borderRadius: "8px" }}>
              <CardContent>
                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
                  <TextField select size="small" label="Formato" value={manualFormat} onChange={(event) => {
                    const nextFormat = event.target.value;
                    setManualFormat(nextFormat);
                    syncManualText(draft, nextFormat);
                  }} sx={{ minWidth: 160 }}>
                    <MenuItem value="yaml">YAML</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                  </TextField>
                  <Button variant="outlined" onClick={parseManual}>Aplicar al borrador</Button>
                  <Alert severity="info" sx={{ py: 0 }}>Los comentarios YAML no se preservan porque la API interna opera sobre configuracion estructurada.</Alert>
                </Stack>
                {manualError && <Alert severity="error" sx={{ mb: 2 }}>{manualError}</Alert>}
                <TextField
                  value={manualText}
                  onChange={(event) => setManualText(event.target.value)}
                  multiline
                  minRows={28}
                  fullWidth
                  spellCheck={false}
                  disabled={loading || saving}
                />
              </CardContent>
            </Card>
          )}

          {tab === "full" && (
            <Card sx={{ borderRadius: "8px" }}>
              <CardContent>
                <Typography variant="h6">Documento completo preservado</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Vista exacta del objeto que se enviara en el PUT.</Typography>
                <Box component="pre" sx={{ p: 1.5, borderRadius: "8px", border: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: (theme) => theme.custom.surface.base, color: "text.primary", fontSize: 12, overflow: "auto", maxHeight: "72vh", fontFamily: (theme) => theme.custom.monoFont }}>
                  {pretty(draft)}
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog open={Boolean(canvas)} onClose={closeCanvas} maxWidth="lg" fullWidth>
        <DialogContent>
          {canvas && <DrawingCanvas mode={canvas.mode} initialData={canvas.initialData} title={canvas.title} onSave={canvas.onSave} onCancel={closeCanvas} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
